/**
 * @file useOpenCreateFromQuery.ts
 * @brief Opens a create dialog when the URL has `?create=1`.
 *
 * @details
 * Cmd+K “Create …” actions navigate to the list page with `create=1`.
 * The page calls this hook so the dialog opens without a shell-level host.
 * Clears the query param after opening so refresh doesn’t re-open.
 *
 * @references
 * - features/command-palette/CommandPalette.tsx
 *
 * @author DES
 * @date 17-07-2026
 */

import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

export function useOpenCreateFromQuery(
  setOpen: (open: boolean) => void,
): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const handledRef = useRef(false);

  useEffect(() => {
    if (searchParams.get("create") !== "1") {
      handledRef.current = false;
      return;
    }
    if (handledRef.current) return;
    handledRef.current = true;
    setOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("create");
    setSearchParams(next, { replace: true });
  }, [searchParams, setOpen, setSearchParams]);
}
